import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Alert, CircularProgress, IconButton, Tooltip, TextField, MenuItem,
  Tabs, Tab, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getBillingByProcessor, BillingByProcessor as Data, BillingProcAccount } from "../services/billing";
import { ACQ_CMF, CUENTAS, matchCuenta, cuentaSufijo, type Cuenta } from "../services/monitoreoVivo";

const nf = new Intl.NumberFormat("es-AR");
const money = (n: number, proc: string) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: proc.startsWith("MX") ? "MXN" : "ARS", maximumFractionDigits: 0 }).format(n || 0);

function Tile({ label, monto, count, color }: { label: string; monto: string; count: number; color?: string }) {
  return (
    <Box sx={{ minWidth: 200, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{monto}</Typography>
      <Typography variant="caption" color="text.secondary">{nf.format(count)} factura(s)</Typography>
    </Box>
  );
}

export default function BillingByProcessor({ period }: { period: string }) {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [proc, setProc] = useState<string>("");
  const [cuenta, setCuenta] = useState<string>("todas");

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setData(await getBillingByProcessor(period)); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { void load(); }, [load]);

  const procs = useMemo(() => (data?.processors ?? []).map((p) => p.processor), [data]);
  const activeProc = procs.includes(proc) ? proc : (procs[0] ?? "");
  useEffect(() => { setCuenta("todas"); }, [activeProc]);

  const procData = useMemo(() => data?.processors.find((p) => p.processor === activeProc), [data, activeProc]);
  const isCmf = activeProc === ACQ_CMF;
  const accts = procData?.accounts ?? [];
  const hayFiltroCuenta = isCmf || accts.length > 1;
  const cuentaActiva = hayFiltroCuenta ? cuenta : "todas";
  const options = isCmf
    ? CUENTAS.map((o) => ({ value: o.value as string, label: o.label }))
    : [{ value: "todas", label: "Todas las cuentas" },
       ...accts.map((a) => ({ value: a.account, label: a.account || "(cuenta por defecto)" }))];
  const match = (a: BillingProcAccount) =>
    cuentaActiva === "todas" ? true
    : isCmf ? matchCuenta(cuentaActiva as Cuenta, a.account)
    : a.account === cuentaActiva;

  const filtered = accts.filter(match);
  const tot = filtered.reduce((t, a) => ({
    fact_count: t.fact_count + a.fact_count, fact_monto: t.fact_monto + a.fact_monto,
    pend_count: t.pend_count + a.pend_count, pend_monto: t.pend_monto + a.pend_monto,
  }), { fact_count: 0, fact_monto: 0, pend_count: 0, pend_monto: 0 });

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Facturación por procesador</Typography>
        <Tooltip title="Refrescar"><span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
        {loading && <CircularProgress size={16} />}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {data && procs.length === 0 && <Typography color="text.secondary">Sin facturación en el período.</Typography>}

      {procData && (
        <Stack spacing={2}>
          <Tabs value={activeProc} onChange={(_e, v) => setProc(v)} variant="scrollable" allowScrollButtonsMobile>
            {data!.processors.map((p) => (
              <Tab key={p.processor} value={p.processor}
                   label={`${p.processor} (${nf.format(p.totals.fact_count + p.totals.pend_count)})`} />
            ))}
          </Tabs>

          {hayFiltroCuenta && (
            <TextField size="small" select label={isCmf ? "Cuenta CMF" : "Cuenta"} value={cuentaActiva}
              sx={{ minWidth: 260, maxWidth: 360 }} onChange={(e) => setCuenta(e.target.value)}>
              {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          )}

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Tile label="Facturado (emitido)" monto={money(tot.fact_monto, activeProc)} count={tot.fact_count} color="#166534" />
            <Tile label="Pendiente (sin emitir)" monto={money(tot.pend_monto, activeProc)} count={tot.pend_count} color="#B45309" />
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Cuenta</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Facturado</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right"># </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Pendiente</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right"># </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin cuentas para el filtro.</TableCell></TableRow>
                ) : filtered.map((a) => (
                  <TableRow key={a.account || "(default)"} hover>
                    <TableCell sx={{ fontFamily: "monospace" }}>{a.account || "(cuenta por defecto)"}</TableCell>
                    <TableCell align="right">{money(a.fact_monto, activeProc)}</TableCell>
                    <TableCell align="right">{nf.format(a.fact_count)}</TableCell>
                    <TableCell align="right" sx={{ color: a.pend_count ? "#B45309" : undefined }}>{money(a.pend_monto, activeProc)}</TableCell>
                    <TableCell align="right">{nf.format(a.pend_count)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                  <TableCell sx={{ fontWeight: 700 }}>TOTAL {activeProc}{cuentaActiva === "todas" ? "" : isCmf ? cuentaSufijo(cuentaActiva as Cuenta) : ` · cta ${cuentaActiva || "por defecto"}`}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{money(tot.fact_monto, activeProc)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{nf.format(tot.fact_count)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{money(tot.pend_monto, activeProc)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{nf.format(tot.pend_count)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Box>
  );
}
